import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { SplaDeviceCores } from '../types/models/Spla';
import type { ApiResponseSplaDeviceCoresList } from '../types/api/ApiResponseSpla';

interface UseAdminSplaDeviceCoresReturn {
  devices: SplaDeviceCores[];
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
  /** Replace one registry row in place (matched by `id`) from a PATCH response. */
  applyDevice: (updated: SplaDeviceCores) => void;
  /** Drop one row after a DELETE. */
  removeDevice: (id: number) => void;
}

// GET /admin/spla/device-cores — the staff registry of PHYSICAL core counts (never vCPUs), by
// device name. Overrides the agent-reported NetBox `core_count` custom field and covers agent-less
// hosts; a core edit propagates on the next bindings recompute. Staff-only; `enabled` defers the
// fetch until the staff check passes. Branch on the JSend `body.status`, NEVER `response.ok`.
export const useAdminSplaDeviceCores = (enabled: boolean): UseAdminSplaDeviceCoresReturn => {
  const [devices, setDevices] = useState<SplaDeviceCores[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/admin/spla/device-cores');
      const body = (await response.json().catch(() => null)) as ApiResponseSplaDeviceCoresList | null;
      if (body?.status === 'success' && body.data?.devices) {
        setDevices(body.data.devices);
      } else {
        setError(body?.message ?? 'Failed to load the device-core registry');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the device-core registry');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const applyDevice = useCallback((updated: SplaDeviceCores) => {
    setDevices((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
  }, []);

  const removeDevice = useCallback((id: number) => {
    setDevices((prev) => prev.filter((d) => d.id !== id));
  }, []);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchDevices();
    }
  }, [enabled, auth.isAuthenticated, fetchDevices]);

  return { devices, loading, error, refetch: fetchDevices, applyDevice, removeDevice };
};
